import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Globe, Plus, Trash2, RefreshCw, Loader2, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebsiteShowcaseManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ plan: 'All', title: '', description: '', liveUrl: '', imageUrl: '', order: 0 });
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/website-showcase`); setItems(Array.isArray(data) ? data : []); }
    catch (e) { toast({ title: 'Failed to load', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title) { toast({ title: 'Title required', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('image', file);
      await axios.post(`${API}/admin/website-showcase`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Showcase added' });
      setOpen(false); setForm({ plan: 'All', title: '', description: '', liveUrl: '', imageUrl: '', order: 0 }); setFile(null);
      await load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const toggleActive = async (item) => {
    try {
      const fd = new FormData();
      fd.append('active', (!item.active).toString());
      await axios.put(`${API}/admin/website-showcase/${item.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
    } catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this showcase item?')) return;
    try { await axios.delete(`${API}/admin/website-showcase/${id}`); toast({ title: 'Deleted' }); await load(); }
    catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Globe className="w-6 h-6 text-purple-500" /> Website Showcase</h2>
          <p className="text-slate-500 text-sm">Add sample dropshipping stores shown on the landing page (plan-wise).</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-1" /> Add Website</Button>
          <Button onClick={load} variant="outline" size="sm"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div> : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400">No showcase items yet. Add your first client store.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className={`rounded-xl border overflow-hidden ${it.active ? 'border-slate-100' : 'border-slate-200 opacity-60'}`}>
              <div className="aspect-video bg-slate-100">{it.imageUrl && <img src={it.imageUrl.startsWith('http') ? it.imageUrl : `${BACKEND_URL}${it.imageUrl}`} alt={it.title} className="w-full h-full object-cover" />}</div>
              <div className="p-3">
                <div className="flex items-center justify-between"><span className="text-xs font-bold text-blue-600">{it.plan}</span>{it.liveUrl && <a href={it.liveUrl} target="_blank" rel="noreferrer" className="text-slate-400"><ExternalLink className="w-4 h-4" /></a>}</div>
                <h3 className="font-semibold text-slate-800 mt-1">{it.title}</h3>
                <p className="text-xs text-slate-400 line-clamp-2">{it.description}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => toggleActive(it)}>{it.active ? <><EyeOff className="w-4 h-4 mr-1" /> Hide</> : <><Eye className="w-4 h-4 mr-1" /> Show</>}</Button>
                  <Button size="sm" variant="outline" onClick={() => remove(it.id)} className="border-red-300 text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Website Showcase</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none">
              {['All', 'Basic', 'Advanced', 'Professional'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Store title" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" rows={2} />
            <input value={form.liveUrl} onChange={(e) => setForm({ ...form, liveUrl: e.target.value })} placeholder="Live URL (optional)" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" />
            <input value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} placeholder="Image URL (or upload below)" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none" />
            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm text-slate-500" />
            <Button onClick={create} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Website'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebsiteShowcaseManagement;
